import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MessageSquareReply } from 'lucide-react';
import { listAuditLog } from '@mkcore/platform/schema';
import { steerRun } from '@mkcore/agents/schema';
import { Badge, Button, Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, ErrorState, Field, Mono, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Textarea, Tr } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime, formatMicros, formatNumber } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { formatDuration } from '@/features/admin/job-status';
import { RunStatusBadge, runQuery, tokenCount } from '@/features/agents/ai';

export const Route = createFileRoute('/_app/agents/runs/$id')({ component: RunDetail });

function Json({ value }: { value: unknown }) {
  return <pre className="max-h-48 overflow-auto rounded-md bg-mono p-2 font-mono text-xs">{JSON.stringify(value, null, 2)}</pre>;
}

const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * One agent run (CO-0971): what started it, its totals against its limits (CO-0837), every model call with tokens and
 * costs (CO-0970), every decision with its chances and the path taken (CO-0973), the trace, and the changes it made
 * in the audit trail (CO-0969).
 */
function RunDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const run = useQuery({ ...runQuery(id), refetchInterval: (q) => (q.state.data?.status === 'running' ? 3000 : false) });
  const canAudit = useCan('admin:read');
  const canSteer = useCan('agents:update');
  const [steering, setSteering] = useState(false);
  const changes = useQuery({ queryKey: ['admin', 'audit', 'run', id], queryFn: () => call(listAuditLog, { agentRunId: id, limit: 100 }), enabled: canAudit });
  if (run.isError) return <ErrorState title={t('errors.generic')} description={message(run.error)} />;
  if (!run.data) return <Skeleton className="h-96" />;
  const r = run.data;
  const limits = Object.entries(r.limits).filter(([, v]) => v != null);
  return (
    <>
      <Link to="/agents/runs" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('agents.runs.title')}
      </Link>
      <PageHeader
        eyebrow={t('agents.runs.title')}
        title={r.task}
        description={
          <span className="grid gap-0.5 text-xs text-fg-3">
            <span>
              {r.agentName ?? t('agents.runs.noAgent')} · {t(`agents.runs.triggers.${r.trigger}`)} · {formatDateTime(r.startedAt)}
              {r.durationMs !== null && ` · ${formatDuration(r.durationMs)}`}
              {r.onBehalfOfName && ` · ${t('agents.runs.onBehalfOf', { name: r.onBehalfOfName })}`}
            </span>
            {r.targetType && (
              <Mono className="text-[11px]">
                {r.targetType} {r.targetId}
              </Mono>
            )}
          </span>
        }
        actions={
          <span className="inline-flex items-center gap-2">
            {canSteer && (r.routineId || r.issueId) && (
              <Button variant="secondary" size="sm" onClick={() => setSteering(true)}>
                <MessageSquareReply /> {t('agents.runs.steer')}
              </Button>
            )}
            <RunStatusBadge status={r.status} />
          </span>
        }
      />
      {steering && <SteerDialog runId={r.id} running={r.status === 'running'} onClose={() => setSteering(false)} />}
      {r.error && (
        <Card className="mb-4 border-danger/40 bg-danger-bg p-4 text-sm text-danger" role="alert">
          <strong>{t('agents.runs.error')}:</strong> {r.error}
        </Card>
      )}
      {r.outcome && (
        <Card className="mb-4 p-4 text-sm">
          <strong>{t('agents.runs.outcome')}:</strong>
          {/* The whole answer (#360): a report of many lines keeps its line breaks. */}
          <div className="mt-1 whitespace-pre-wrap break-words">{r.outcome}</div>
        </Card>
      )}
      <dl className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          [t('agents.runs.calls'), formatNumber(r.calls)],
          [t('agents.runs.tokens'), tokenCount(r.inputTokens, r.outputTokens)],
          [t('agents.runs.cost'), formatMicros(r.costMicros, r.currency)],
          [t('agents.runs.toolCalls'), formatNumber(r.toolCalls)],
        ].map(([k, v]) => (
          <Card key={k} className="grid gap-0.5 p-3">
            <dt className="text-xs text-fg-3">{k}</dt>
            <dd className="text-lg font-bold tabular">{v}</dd>
          </Card>
        ))}
      </dl>
      {limits.length > 0 && (
        <p className="mb-6 text-xs text-fg-3">
          {t('agents.runs.limits')}: {limits.map(([k, v]) => `${t(`agents.runs.limit.${k}`)} ${formatNumber(Number(v))}`).join(' · ')}
        </p>
      )}

      <h2 className="mb-2 text-lg font-bold">{t('agents.runs.modelCalls')}</h2>
      {!r.runCalls.length ? (
        <p className="mb-6 text-sm text-fg-3">{t('agents.runs.noCalls')}</p>
      ) : (
        <div className="mb-6">
          <Table>
            <THead>
              <Tr>
                <Th>{t('agents.runs.model')}</Th>
                <Th>{t('common.status')}</Th>
                <Th className="hidden md:table-cell">{t('agents.runs.task')}</Th>
                <Th className="text-right">{t('agents.runs.tokens')}</Th>
                <Th className="text-right">{t('agents.runs.cost')}</Th>
                <Th className="hidden md:table-cell text-right">{t('agents.runs.duration')}</Th>
              </Tr>
            </THead>
            <TBody>
              {r.runCalls.map((c) => (
                <Tr key={c.id}>
                  <Td>
                    <div className="font-bold">{c.alias ?? c.provider}</div>
                    <Mono className="text-[11px]">
                      {c.provider}:{c.modelId}
                    </Mono>
                  </Td>
                  <Td>
                    <Badge tone={c.status === 'ok' ? 'success' : c.status === 'invalid' ? 'warning' : 'danger'}>{t(`agents.runs.callStatus.${c.status}`)}</Badge>
                    {c.error && <div className="mt-1 max-w-xs truncate text-xs text-danger" title={c.error}>{c.error}</div>}
                  </Td>
                  <Td className="hidden md:table-cell text-xs text-fg-2">
                    {t(`agents.runs.kind.${c.kind}`)} · {c.task}
                  </Td>
                  <Td className="text-right tabular text-fg-2">{tokenCount(c.inputTokens, c.outputTokens)}</Td>
                  <Td className="text-right tabular">{formatMicros(c.costMicros, c.currency)}</Td>
                  <Td className="hidden md:table-cell text-right tabular text-fg-2">{formatDuration(c.durationMs)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      {r.feedback.length > 0 && (
        <section className="mb-6" aria-label={t('agents.runs.feedback')}>
          <h2 className="mb-2 text-lg font-bold">{t('agents.runs.feedback')}</h2>
          <div className="grid gap-3">
            {r.feedback.map((f) => (
              <Card key={f.id} className="grid gap-2 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={f.label === 'approved' ? 'success' : f.label === 'rejected' ? 'danger' : 'warning'}>{t(`agents.runs.feedbackLabel.${f.label}`)}</Badge>
                  {f.tool ? <Mono>{f.tool}</Mono> : f.kind && <Mono>{f.kind}</Mono>}
                  {f.agentRevision !== null && <span className="text-fg-3">{t('agents.runs.revision', { n: f.agentRevision })}</span>}
                  <span className="text-fg-3">
                    {f.decidedByName ?? '–'} · {formatDateTime(f.at)}
                  </span>
                </div>
                {f.comment && <p className="whitespace-pre-wrap">{f.comment}</p>}
                {f.correction && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <p className="text-xs text-fg-2">{t('agents.runs.proposed')}</p>
                      <pre className="overflow-x-auto rounded-md bg-sunken p-2 text-xs">{JSON.stringify(f.correction.before, null, 2)}</pre>
                    </div>
                    <div>
                      <p className="text-xs text-fg-2">{t('agents.runs.approvedAs')}</p>
                      <pre className="overflow-x-auto rounded-md bg-sunken p-2 text-xs">{JSON.stringify(f.correction.after, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>
      )}

      {r.decisions.length > 0 && (
        <>
          <h2 className="mb-2 text-lg font-bold">{t('agents.runs.decisions')}</h2>
          <div className="mb-6 grid gap-3">
            {r.decisions.map((d) => (
              <Card key={d.id} className="grid gap-2 p-3 text-sm" aria-label={d.task}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold">{d.task}</span>
                  <Badge tone="outline">{d.engine === 'jev' ? 'Jev' : t('agents.runs.llmFallback')}</Badge>
                  <Badge tone={d.confidence >= d.threshold ? 'success' : 'warning'}>{t('agents.runs.confidence', { value: pct(d.confidence), threshold: pct(d.threshold) })}</Badge>
                  {d.path && <span className="text-xs text-fg-3">{t('agents.runs.path', { path: d.path })}</span>}
                </div>
                <ul className="grid gap-1">
                  {Object.entries(d.answers).map(([q, a]) => (
                    <li key={q}>
                      <Mono className="text-xs">{q}</Mono> → <strong>{String(a.value)}</strong>
                      {a.probabilities && (
                        <span className="text-xs text-fg-3">
                          {' '}
                          (
                          {Object.entries(a.probabilities)
                            .map(([k, p]) => `${k} ${pct(p)}`)
                            .join(', ')}
                          )
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </>
      )}

      {r.events.length > 0 && (
        <>
          <h2 className="mb-2 text-lg font-bold">{t('agents.runs.trace')}</h2>
          <ol className="mb-6 grid gap-2">
            {r.events.map((e) => (
              <li key={e.id} className="grid gap-1 rounded-md border border-line-subtle p-2 text-sm">
                <span className="flex flex-wrap items-center gap-2">
                  <Badge tone={e.kind === 'error' ? 'danger' : 'outline'}>{t(`agents.runs.event.${e.kind}`)}</Badge>
                  {e.name && <Mono className="text-xs">{e.name}</Mono>}
                  <span className="text-xs text-fg-3">{formatDateTime(e.at)}</span>
                </span>
                {e.data != null && <Json value={e.data} />}
              </li>
            ))}
          </ol>
        </>
      )}

      {canAudit && (
        <>
          <h2 className="mb-2 text-lg font-bold">{t('agents.runs.changes')}</h2>
          {!changes.data ? (
            <Skeleton className="h-16" />
          ) : !changes.data.items.length ? (
            <p className="text-sm text-fg-3">{t('agents.runs.noChanges')}</p>
          ) : (
            <Table>
              <THead>
                <Tr>
                  <Th>{t('agents.runs.action')}</Th>
                  <Th className="hidden md:table-cell">{t('agents.runs.object')}</Th>
                  <Th>{t('common.created')}</Th>
                </Tr>
              </THead>
              <TBody>
                {changes.data.items.map((a) => (
                  <Tr key={a.id}>
                    <Td>
                      <Mono className="text-xs">{a.action}</Mono>
                    </Td>
                    <Td className="hidden md:table-cell text-xs text-fg-2">
                      {a.entityType} {a.entityId}
                    </Td>
                    <Td className="whitespace-nowrap tabular text-fg-2">{formatDateTime(a.occurredAt)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </>
      )}
    </>
  );
}

/** Steer the agent (CO-1270): a running run stops, and a follow-up starts in the same session with the instruction. */
function SteerDialog({ runId, running, onClose }: { runId: string; running: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const steer = useActionMutation(steerRun, {
    invalidate: [['agent']],
    success: t('agents.runs.steerDone'),
    onSuccess: (r) => {
      onClose();
      void navigate({ to: '/agents/runs/$id', params: { id: r.runId } });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('agents.runs.steerTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-fg-2">{running ? t('agents.runs.steerRunning') : t('agents.runs.steerFollowUp')}</p>
        <Field label={t('agents.runs.steerMessage')}>{(p) => <Textarea {...p} rows={4} value={text} onChange={(e) => setText(e.target.value)} />}</Field>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!text.trim()} loading={steer.isPending} onClick={() => steer.mutate({ input: { id: runId, message: text.trim() } })}>
            {t('agents.runs.steerSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
